import React, { useState, useMemo } from "react";
import { PROFILE_TYPES, PARTICULIER_CATS, PRO_FAMILIES, PRO_METIERS } from "./constants";

// Sélecteur « Qui suis-je » (nouvelle taxonomie unifiée, cf. docs/TAXONOMIE-UNIFIEE.md).
// Contrôlé : props { profile, selected, onChange, lang, dark }.
//  - profile : "particulier" | "pro" | null
//  - selected : tableau d'ids (sous-cats particulier OU métiers pro)
//  - onChange(profile, selected)
//  - dark : fond sombre (inscription) → texte clair.
// 2 profils → si particulier : liste courte ; si pro : familles + recherche + multi-sélection.

const UI = {
  fr:{ part:"Particulier / Cavalier", pro:"Pro / Entreprise", partSub:"Cavalier, parent, propriétaire…", proSub:"Écurie, maréchal, véto, transporteur…", partHint:"Tu es…", proHint:"Choisis ton ou tes métier(s)", multi:"Sélection multiple possible", search:"Rechercher un métier…", none:"Aucun métier trouvé", selectedN:(n)=>`${n} sélectionné${n>1?"s":""}` },
  en:{ part:"Individual / Rider", pro:"Pro / Business", partSub:"Rider, parent, owner…", proSub:"Stable, farrier, vet, transporter…", partHint:"You are…", proHint:"Choose your profession(s)", multi:"Multiple selection possible", search:"Search a profession…", none:"No profession found", selectedN:(n)=>`${n} selected` },
  de:{ part:"Privatperson / Reiter", pro:"Profi / Unternehmen", partSub:"Reiter, Eltern, Besitzer…", proSub:"Stall, Hufschmied, Tierarzt…", partHint:"Du bist…", proHint:"Wähle deinen Beruf / deine Berufe", multi:"Mehrfachauswahl möglich", search:"Beruf suchen…", none:"Kein Beruf gefunden", selectedN:(n)=>`${n} ausgewählt` },
  es:{ part:"Particular / Jinete", pro:"Pro / Empresa", partSub:"Jinete, padre, propietario…", proSub:"Cuadra, herrador, veterinario…", partHint:"Eres…", proHint:"Elige tu(s) profesión(es)", multi:"Selección múltiple posible", search:"Buscar una profesión…", none:"Ninguna profesión encontrada", selectedN:(n)=>`${n} seleccionado${n>1?"s":""}` },
  it:{ part:"Privato / Cavaliere", pro:"Pro / Azienda", partSub:"Cavaliere, genitore, proprietario…", proSub:"Scuderia, maniscalco, veterinario…", partHint:"Sei…", proHint:"Scegli la/le tua/e professione/i", multi:"Selezione multipla possibile", search:"Cerca una professione…", none:"Nessuna professione trovata", selectedN:(n)=>`${n} selezionato/i` },
  pt:{ part:"Particular / Cavaleiro", pro:"Pro / Empresa", partSub:"Cavaleiro, pai, proprietário…", proSub:"Cavalariça, ferrador, veterinário…", partHint:"És…", proHint:"Escolhe a(s) tua(s) profissão(ões)", multi:"Seleção múltipla possível", search:"Procurar uma profissão…", none:"Nenhuma profissão encontrada", selectedN:(n)=>`${n} selecionado(s)` },
  nl:{ part:"Particulier / Ruiter", pro:"Pro / Onderneming", partSub:"Ruiter, ouder, eigenaar…", proSub:"Stal, hoefsmid, dierenarts…", partHint:"Je bent…", proHint:"Kies je beroep(en)", multi:"Meerdere keuzes mogelijk", search:"Een beroep zoeken…", none:"Geen beroep gevonden", selectedN:(n)=>`${n} geselecteerd` },
};

const DIACRITICS = new RegExp("[\\u0300-\\u036f]", "g");
const norm = (s) => (s || "").toLowerCase().normalize("NFD").replace(DIACRITICS, "");

export default function IdentityPicker({ profile = null, selected = [], onChange, lang = "fr", dark = false, hideProfiles = false }) {
  const t = UI[lang] || UI.fr;
  const L = (i18n) => i18n[lang] || i18n.fr;
  const [search, setSearch] = useState("");
  const [openFam, setOpenFam] = useState({}); // familyId -> bool

  const txt = dark ? "rgba(255,255,255,.92)" : "var(--text)";
  const muted = dark ? "rgba(255,255,255,.55)" : "var(--muted)";
  const cardBg = dark ? "rgba(255,255,255,.06)" : "#fff";
  const cardBorder = dark ? "rgba(255,255,255,.14)" : "var(--border)";

  const setProfile = (p) => { if (p !== profile) onChange(p, []); };
  const toggle = (id) => {
    const has = selected.includes(id);
    onChange(profile, has ? selected.filter(x => x !== id) : [...selected, id]);
  };

  // Recherche pro : liste plate des métiers correspondants (avec leur famille).
  const searchResults = useMemo(() => {
    if (profile !== "pro" || !search.trim()) return null;
    const q = norm(search);
    const res = [];
    PRO_FAMILIES.forEach(fam => fam.metiers.forEach(m => {
      if (norm(L(m.i18n)).includes(q)) res.push({ ...m, famLabel: L(fam.i18n) });
    }));
    return res;
  }, [profile, search, lang]);

  const chip = (active) => ({
    display:"inline-flex", alignItems:"center", gap:6, padding:"9px 13px", borderRadius:20,
    fontSize:13, fontWeight:600, cursor:"pointer", userSelect:"none", transition:"all .12s",
    border:`1.5px solid ${active ? "#C8001A" : cardBorder}`,
    background: active ? "#C8001A" : cardBg,
    color: active ? "#fff" : txt,
  });

  return (
    <div style={{ display:"flex", flexDirection:"column", gap:14 }}>
      {/* 2 profils */}
      {!hideProfiles && <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:10 }}>
        {[
          { id:"particulier", title:t.part, sub:t.partSub },
          { id:"pro", title:t.pro, sub:t.proSub },
        ].map(p => {
          const on = profile === p.id;
          return (
            <button key={p.id} type="button" onClick={()=>setProfile(p.id)} style={{
              textAlign:"left", padding:"14px 14px", borderRadius:14, cursor:"pointer",
              border:`2px solid ${on ? "#C8001A" : cardBorder}`,
              background: on ? "rgba(200,0,26,.08)" : cardBg,
              boxShadow: on ? "0 4px 14px rgba(200,0,26,.18)" : "none",
            }}>
              <div style={{ fontSize:14.5, fontWeight:800, color: on ? "#C8001A" : txt, fontFamily:"var(--fh)" }}>{p.title}</div>
              <div style={{ fontSize:11, color:muted, marginTop:3, lineHeight:1.4 }}>{p.sub}</div>
            </button>
          );
        })}
      </div>}

      {/* PARTICULIER : liste courte */}
      {profile === "particulier" && (
        <div>
          <div style={{ fontSize:12, color:muted, marginBottom:8 }}>{t.partHint} <span style={{ opacity:.7 }}>({t.multi.toLowerCase()})</span></div>
          <div style={{ display:"flex", flexWrap:"wrap", gap:8 }}>
            {PARTICULIER_CATS.map(c => (
              <div key={c.id} onClick={()=>toggle(c.id)} style={chip(selected.includes(c.id))}>{L(c.i18n)}</div>
            ))}
          </div>
        </div>
      )}

      {/* PRO : recherche + familles + multi-sélection */}
      {profile === "pro" && (
        <div style={{ display:"flex", flexDirection:"column", gap:10 }}>
          <div style={{ fontSize:12, color:muted }}>{t.proHint} · <span style={{ color:"#C8001A", fontWeight:700 }}>{t.multi}</span></div>

          {/* chips sélectionnés */}
          {selected.length > 0 && (
            <div style={{ display:"flex", flexWrap:"wrap", gap:6 }}>
              {selected.map(id => PRO_METIERS[id] && (
                <div key={id} onClick={()=>toggle(id)} style={{ ...chip(true), paddingRight:9 }}>
                  {L(PRO_METIERS[id].i18n)} <span style={{ fontSize:15, lineHeight:1, marginLeft:2 }}>×</span>
                </div>
              ))}
            </div>
          )}

          {/* recherche */}
          <input
            value={search} onChange={e=>setSearch(e.target.value)} placeholder={t.search}
            style={{ width:"100%", boxSizing:"border-box", padding:"11px 14px", borderRadius:10, fontSize:13,
              background: cardBg, color:txt, border:`1.5px solid ${cardBorder}` }}
          />

          {/* résultats de recherche (liste plate) OU familles (accordéon) */}
          {searchResults ? (
            searchResults.length === 0 ? (
              <div style={{ fontSize:13, color:muted, padding:"8px 2px" }}>{t.none}</div>
            ) : (
              <div style={{ display:"flex", flexWrap:"wrap", gap:8 }}>
                {searchResults.map(m => (
                  <div key={m.id} onClick={()=>toggle(m.id)} style={chip(selected.includes(m.id))}>
                    {L(m.i18n)} <span style={{ fontSize:10, opacity:.6, marginLeft:2 }}>· {m.famLabel}</span>
                  </div>
                ))}
              </div>
            )
          ) : (
            <div style={{ display:"flex", flexDirection:"column", gap:6 }}>
              {PRO_FAMILIES.map(fam => {
                const isOpen = !!openFam[fam.id];
                const nSel = fam.metiers.filter(m => selected.includes(m.id)).length;
                return (
                  <div key={fam.id} style={{ border:`1px solid ${cardBorder}`, borderRadius:10, overflow:"hidden" }}>
                    <div onClick={()=>setOpenFam(o=>({ ...o, [fam.id]: !isOpen }))} style={{
                      display:"flex", alignItems:"center", justifyContent:"space-between", gap:8,
                      padding:"11px 13px", cursor:"pointer", background: cardBg,
                    }}>
                      <span style={{ fontSize:13.5, fontWeight:700, color:txt }}>
                        {L(fam.i18n)}{nSel > 0 && <span style={{ color:"#C8001A", marginLeft:6, fontSize:12 }}>({nSel})</span>}
                      </span>
                      <span style={{ color:muted, fontSize:12, transform: isOpen ? "rotate(90deg)" : "none", transition:"transform .15s" }}>▶</span>
                    </div>
                    {isOpen && (
                      <div style={{ display:"flex", flexWrap:"wrap", gap:8, padding:"4px 12px 12px" }}>
                        {fam.metiers.map(m => (
                          <div key={m.id} onClick={()=>toggle(m.id)} style={chip(selected.includes(m.id))}>{L(m.i18n)}</div>
                        ))}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}

          {selected.length > 0 && (
            <div style={{ fontSize:11.5, color:muted, textAlign:"right" }}>{t.selectedN(selected.length)}</div>
          )}
        </div>
      )}
    </div>
  );
}
